<template>
  <div :class="componentClasses">
    <label class="stk-toggle-label">
      <input
        class="stk-toggle-input"
        type="checkbox"
        :checked="isChecked"
        :disabled="disabled || loading"
        @change="handleToggle"
      >
      <span class="stk-toggle-slider">
        <Loader v-if="loading" />
      </span>
    </label>
    <slot />
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue';
import Loader from '@stakeui/graphic/loader/Loader.vue';

const props = defineProps({
  /* Mode 1: v-model */
  modelValue: { type: Boolean, default: undefined },

  /* Mode 2: controlled without v-model */
  checked: { type: Boolean, default: undefined },

  /* Mode 3: uncontrolled (internal state) */
  defaultChecked: { type: Boolean, default: false },

  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:checked',
  'change', // emit(next, event)
  'toggle', // alias event
]);

// Internal state for uncontrolled mode
const inner = ref(props.defaultChecked);

// Which mode?
const isVModel = computed(() => props.modelValue !== undefined);
const isCheckedProp = computed(() => props.checked !== undefined);

// Single source of truth
const isChecked = computed(() => {
  if (isVModel.value) {
    // v-model is being used
    return props.modelValue;
  }

  if (isCheckedProp.value) {
    // controlled via :checked + @change
    return props.checked;
  }

  // uncontrolled (internal state)
  return inner.value;
});

const handleToggle = event => {
  const next = !isChecked.value;

  // Fire generic events so parent can handle however it wants
  emit('change', next, event);
  emit('toggle', next, event);

  // Sync depending on mode
  if (isVModel.value) {
    emit('update:modelValue', next);
  } else if (isCheckedProp.value) {
    emit('update:checked', next);
  } else {
    inner.value = next; // uncontrolled
  }
};

const componentClasses = computed(() => [
  'stk-toggle',
  isChecked.value ? 'is-checked' : '',
  props.loading ? 'is-loading' : '',
]);
</script>

<style>
.stk-toggle {
  align-items: center;
  display: inline-flex;
  gap: 0.5em;
  vertical-align: top;
}

.stk-toggle-label {
  background-color: var(--stk-border);
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  padding: 0.25em;
  position: relative;
  width: 2.5em;
}

.stk-toggle-input {
  opacity: 0;
  height: 0;
  width: 0;
}

.stk-toggle-slider {
  background-color: var(--stk-text-weak);
  border-radius: 9999px;
  cursor: pointer;
  display: flex;
  height: 1em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, transform;
  width: 1em;
}

/* Loading state */
.stk-toggle.is-loading .stk-toggle-slider {
  background-color: transparent !important;
}

/* Checked state */
.stk-toggle.is-checked .stk-toggle-label {
  background-color: var(--stk-primary-85);
}

.stk-toggle.is-checked .stk-toggle-slider {
  background-color: var(--stk-primary);
  transform: translateX(1em);
}
</style>
